import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormikContext } from "formik";

import useSection from "common/routes/sections/sections.hooks";
import { removeAllSessionStorageForUndoRedo } from "features/budget/workforce/views/employee-data-tab/state/undo-redo-utils";

import { BUDGET_STEPS_ROUTES } from "features/budget/budget.sections";
import { getRequestingNext } from "features/budget/wizard/state/wizard.selectors";
import {
  changeCallingNextCallback,
  changeFetching,
  changeRequestingNext,
  changeScenarioName,
  changeSubmitting,
  changeValid,
} from "features/budget/wizard/state/wizard.actions";
import {
  getBudget,
  // eslint-disable-next-line camelcase
  LEGACY_getBudget,
} from "features/budget/wizard/state/wizard.storage";
import steps from "features/budget/wizard/state/stepper.data";
import { getBudgetMeta } from "features/budget/start/state/meta.storage";
import {
  ASSUMPTION_KEY,
  IS_WORKFORCE_SCENARIO,
  OWNERS_KEY,
  SCHEDULES_KEY,
  TEMPLATES_KEY,
} from "features/budget/budget.constants";
import isMSLegacy from "common/utils/is-ms-legacy";
import { getLocalStorageItem } from "common/utils/storage/local-storage";

export function useStep() {
  const section = useSection();
  return useMemo(() => {
    const { step } = section;
    if (step === undefined) {
      return 0;
    }
    return step;
  }, [section]);
}

export function useRedirectToStep() {
  const history = useHistory();

  return useCallback(
    (step) => {
      const newStep = BUDGET_STEPS_ROUTES[step];

      if (newStep !== undefined) {
        history.push(newStep);
      }
    },
    [history],
  );
}

export function useRedirectToNext() {
  const step = useStep();
  const isWorkforceScenario = getLocalStorageItem(IS_WORKFORCE_SCENARIO);

  const redirectTo = useRedirectToStep();
  const nextStep =
    (step === 2 || step === 5) && isWorkforceScenario === "false"
      ? step + 2
      : step + 1;

  if (step === 6) {
    removeAllSessionStorageForUndoRedo();
  }
  return useCallback(() => redirectTo(nextStep), [redirectTo, nextStep]);
}

export function useWizardForm() {
  const dispatch = useDispatch();
  const { submitForm, isValid, isSubmitting } = useFormikContext();

  const isRequestingNext = useSelector(getRequestingNext);
  useEffect(() => {
    async function submit() {
      await submitForm();
    }
    if (isRequestingNext === true) {
      submit();
    }
  }, [isRequestingNext, submitForm]);

  useEffect(() => {
    dispatch(changeValid(isValid));
  }, [dispatch, isValid]);

  useEffect(() => {
    dispatch(changeSubmitting(isSubmitting));
  }, [dispatch, isSubmitting]);
}

export function useValidatePrevious() {
  const [result, setResult] = useState({
    showDialog: false,
    redirectName: "",
    redirectStep: null,
  });
  const currentStep = useStep();

  useEffect(() => {
    async function validate() {
      let budget;
      if (isMSLegacy() === true) {
        budget = LEGACY_getBudget();
      } else {
        budget = await getBudget();
      }
      for (let i = 0; i < currentStep; i += 1) {
        const { caption: stepTitle, key } = steps[i];
        if (
          ![ASSUMPTION_KEY, SCHEDULES_KEY, TEMPLATES_KEY, OWNERS_KEY].includes(
            key,
          ) &&
          (budget === undefined || budget[steps[i].idbKey] === undefined)
        ) {
          setResult({
            showDialog: true,
            redirectName: stepTitle,
            redirectStep: key,
          });
          return;
        }
      }
    }

    validate();
  }, [currentStep]);

  return result;
}

export function useScenarioHeaderTitle() {
  const dispatch = useDispatch();
  useEffect(() => {
    async function getHeader() {
      const formValues = await getBudgetMeta();
      if (formValues !== undefined) {
        const { scenario } = formValues;
        dispatch(changeScenarioName(scenario));
      }
    }

    getHeader();
  }, [dispatch]);
}

export function useChangeFetching(loading) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (loading) {
      dispatch(changeFetching(true));
    } else {
      dispatch(changeFetching(false));
    }
  }, [dispatch, loading]);
}

/**
 *
 * @param {() => Promise<boolean>} callback
 */
export function useRequestingNext(callback = null) {
  const dispatch = useDispatch();
  const redirect = useRedirectToNext();

  const isRequestingNext = useSelector(getRequestingNext);
  useEffect(() => {
    async function requestingNext() {
      if (isRequestingNext === true) {
        dispatch(changeRequestingNext(false));

        let shouldGoNext = true;
        if (callback !== null && typeof callback === "function") {
          dispatch(changeCallingNextCallback(true));
          shouldGoNext = await callback();
          dispatch(changeCallingNextCallback(false));
        }
        if (shouldGoNext !== false) {
          redirect();
        }
      }
    }
    requestingNext();
  }, [callback, dispatch, isRequestingNext, redirect]);
}
